import _slicedToArray from "/home/shipsy/crm-logistics-frontend/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { Linear } from '@antv/scale';
import { lowerFirst } from '@antv/util';
import { extent } from 'd3-array';
import { indexOf } from '../utils/array';
import { isPolar, isTranspose } from '../utils/coordinate';
import { angle, angleWithQuadrant, dist, sub } from '../utils/vector';
export function applyStyle(selection, style) {
  for (var _i = 0, _Object$entries = Object.entries(style); _i < _Object$entries.length; _i++) {
    var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
      key = _Object$entries$_i[0],
      value = _Object$entries$_i[1];
    selection.style(key, value);
  }
}
/**
 * Draw polygon path with points.
 * @param path
 * @param points
 */
export function appendPolygon(path, points) {
  points.forEach(function (p, idx) {
    return idx === 0 ? path.moveTo(p[0], p[1]) : path.lineTo(p[0], p[1]);
  });
  path.closePath();
  return path;
}
/**
 * Draw arrow between `from` and `to`.
 * @param from
 * @param to
 * @returns
 */
export function arrowPoints(from, to, options) {
  var arrowSize = options.arrowSize;
  var size = typeof arrowSize === 'string' ? +parseFloat(arrowSize) / 100 * dist(from, to) : arrowSize;
  // TODO Use config from style.
  // Default arrow rotate is 30°.
  var arrowAngle = Math.PI / 6;
  var angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
  var arrowAngle1 = Math.PI / 2 - angle - arrowAngle;
  var arrow1 = [to[0] - size * Math.sin(arrowAngle1), to[1] - size * Math.cos(arrowAngle1)];
  var arrowAngle2 = angle - arrowAngle;
  var arrow2 = [to[0] - size * Math.cos(arrowAngle2), to[1] - size * Math.sin(arrowAngle2)];
  return [arrow1, arrow2];
}
/**
 * Draw arc by from -> to, with center and radius.
 * @param path
 * @param from
 * @param to
 * @param center
 * @param radius
 */
export function appendArc(path, from, to, center, radius) {
  var startAngle = angle(sub(center, from)) + Math.PI;
  var endAngle = angle(sub(center, to)) + Math.PI;
  path.arc(center[0], center[1], radius, startAngle, endAngle, endAngle - startAngle < 0);
  return path;
}
/**
 * @todo Fix wrong key point.
 */
export function computeGradient(C, X, Y) {
  var from = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'y';
  var mode = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 'between';
  var P = from === 'y' || from === true ? Y : X;
  var theta = from === 'y' || from === true ? 90 : 0;
  var I = indexOf(P);
  var _extent = extent(I, function (i) {
      return P[i];
    }),
    _extent2 = _slicedToArray(_extent, 2),
    min = _extent2[0],
    max = _extent2[1]; // This need to improve for non-uniform distributed colors.
  var p = new Linear({
    domain: [min, max],
    range: [0, 100]
  });
  var percentage = function percentage(i) {
    return p.map(P[i]);
  };
  var gradientMode = {
    // Interpolate the colors for this segment.
    between: function between(i) {
      return "".concat(C[i], " ").concat(percentage(i), "%");
    },
    // Use the color of the start point as the color for this segment.
    start: function start(i) {
      return i === 0 ? "".concat(C[i], " ").concat(percentage(i), "%") : "".concat(C[i - 1], " ").concat(percentage(i), "%, ").concat(C[i], " ").concat(percentage(i), "%");
    },
    // Use the color of the end point as the color for this segment.
    end: function end(i) {
      return i === C.length - 1 ? "".concat(C[i], " ").concat(percentage(i), "%") : "".concat(C[i], " ").concat(percentage(i), "%, ").concat(C[i + 1], " ").concat(percentage(i), "%");
    }
  };
  var gradient = I.sort(function (a, b) {
    return percentage(a) - percentage(b);
  }).map(gradientMode[mode] || gradientMode['between']).join(',');
  return "linear-gradient(".concat(theta, "deg, ").concat(gradient, ")");
}
export function reorder(points) {
  var _points = _slicedToArray(points, 4),
    p0 = _points[0],
    p1 = _points[1],
    p2 = _points[2],
    p3 = _points[3];
  return [p3, p0, p1, p2];
}
export function getArcObject(coordinate, points, Y) {
  var _ref = isTranspose(coordinate) ? reorder(points) : points,
    _ref2 = _slicedToArray(_ref, 4),
    p0 = _ref2[0],
    p1 = _ref2[1],
    p3 = _ref2[3];
  var _Y = _slicedToArray(Y, 2),
    y = _Y[0],
    y1 = _Y[1];
  var center = coordinate.getCenter();
  var a1 = angleWithQuadrant(sub(p0, center));
  var a2 = angleWithQuadrant(sub(p1, center));
  // There are two situations that a2 === a1:
  // 1. a1 - a2 = 0
  // 2. |a1 - a2| = Math.PI * 2
  // Distinguish them by y and y1:
  var a3 = a2 === a1 && y !== y1 ? a2 + Math.PI * 2 : a2;
  return {
    startAngle: a1,
    endAngle: a3 - a1 >= 0 ? a3 : Math.PI * 2 + a3,
    innerRadius: dist(p3, center),
    outerRadius: dist(p0, center)
  };
}
/**
 * Pick connectStyle from style.
 * @param style
 */
export function getConnectStyle(style) {
  var PREFIX = 'connect';
  return Object.fromEntries(Object.entries(style).filter(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 1),
      key = _ref4[0];
    return key.startsWith(PREFIX);
  }).map(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
      key = _ref6[0],
      value = _ref6[1];
    return [lowerFirst(key.replace(PREFIX, '').trim()), value];
  }).filter(function (_ref7) {
    var _ref8 = _slicedToArray(_ref7, 1),
      key = _ref8[0];
    return key !== undefined;
  }));
}
export function toOpacityKey(options) {
  var colorAttribute = options.colorAttribute,
    _options$opacityAttri = options.opacityAttribute,
    opacityAttribute = _options$opacityAttri === void 0 ? colorAttribute : _options$opacityAttri;
  return "".concat(opacityAttribute, "Opacity");
}
export function getTransform(coordinate, value) {
  if (!isPolar(coordinate)) return '';
  var center = coordinate.getCenter();
  var suffix = value.transform;
  return "translate(".concat(center[0], ", ").concat(center[1], ") ").concat(suffix || '');
}
export function getOrigin(points) {
  if (points.length === 1) return points[0];
  var _points2 = _slicedToArray(points, 2),
    _points2$ = _slicedToArray(_points2[0], 3),
    x0 = _points2$[0],
    y0 = _points2$[1],
    _points2$$ = _points2$[2],
    z0 = _points2$$ === void 0 ? 0 : _points2$$,
    _points2$2 = _slicedToArray(_points2[1], 3),
    x2 = _points2$2[0],
    y2 = _points2$2[1],
    _points2$2$ = _points2$2[2],
    z2 = _points2$2$ === void 0 ? 0 : _points2$2$;
  return [(x0 + x2) / 2, (y0 + y2) / 2, (z0 + z2) / 2];
}